import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Search, Play, RefreshCw, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

/**
 * TaxpayerMatchingPanel - UNL 납세자 매칭 (1차 목업)
 *
 * 상단: BL 리스트 (수입자상호(영문) / 수입자 상호 / 사업자번호 / 연락처 / 주소)
 * 하단: 선택된 BL 기준 유사도검색 결과 (관세청/홈택스 데이터 스위치)
 *
 * 하네스 5단계: Lookup → Enrich → Judge → Score → Persist
 */
const STATUS_META = {
  FOUND_BIZ_NO: { label: '자동확정 후보', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  CONTACT_ONLY: { label: '연락처 확인 필요', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  NOT_FOUND: { label: '수동확인 필요', cls: 'bg-rose-100 text-rose-700 border-rose-300' },
  ERROR: { label: '오류', cls: 'bg-rose-200 text-rose-800 border-rose-400' },
  PENDING: { label: '대기', cls: 'bg-gray-100 text-gray-600 border-gray-300' }
};
const ADDR_META = {
  '본사주소': 'bg-sky-100 text-sky-700 border-sky-300',
  '공장주소': 'bg-orange-100 text-orange-700 border-orange-300',
  '지점주소': 'bg-pink-100 text-pink-700 border-pink-300',
  '미확인': 'bg-gray-100 text-gray-600 border-gray-300'
};

const Badge = ({ text, cls }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${cls}`}>{text}</span>
);

const TaxpayerMatchingPanel = ({ apiBaseUrl = '' }) => {
  const [rows, setRows] = useState([]); // BL 레코드 + final 결과
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [similarityQuery, setSimilarityQuery] = useState('');
  const [similarityResults, setSimilarityResults] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [showOnlyPending, setShowOnlyPending] = useState(false);
  const [customsOn, setCustomsOn] = useState(false);
  const [hometaxOn, setHometaxOn] = useState(false);

  const api = useCallback((path, opts = {}) => fetch(`${apiBaseUrl}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...opts
  }).then(r => r.json()), [apiBaseUrl]);

  // 초기 샘플 로드
  useEffect(() => {
    api('/api/taxpayer/samples').then(data => {
      const init = (data.records || []).map(r => ({
        ...r,
        status: 'PENDING',
        loading: false,
        final: null,
        candidates: []
      }));
      setRows(init);
    }).catch(e => console.error('[Taxpayer] samples load fail', e));
  }, [api]);

  const runAll = async () => {
    if (!rows.length) return;
    setLoadingAll(true);
    setRows(prev => prev.map(r => ({ ...r, loading: true })));
    try {
      const data = await api('/api/taxpayer/match-batch', {
        method: 'POST',
        body: JSON.stringify({ records: rows })
      });
      setRows(prev => prev.map((r, i) => {
        const res = data.results?.[i];
        if (!res) return { ...r, loading: false };
        return { ...r, loading: false, status: res.final?.status || 'NOT_FOUND', final: res.final, candidates: res.candidates || [] };
      }));
    } finally { setLoadingAll(false); }
  };

  const runOne = async (idx) => {
    const r = rows[idx];
    setRows(prev => prev.map((x, i) => i === idx ? { ...x, loading: true } : x));
    try {
      const res = await api('/api/taxpayer/match', { method: 'POST', body: JSON.stringify(r) });
      setRows(prev => prev.map((x, i) => i === idx ? { ...x, loading: false, status: res.final?.status || 'NOT_FOUND', final: res.final, candidates: res.candidates || [] } : x));
    } catch {
      setRows(prev => prev.map((x, i) => i === idx ? { ...x, loading: false, status: 'ERROR' } : x));
    }
  };

  const runSimilarity = async (q) => {
    const query = (q ?? similarityQuery).trim();
    if (!query) { setSimilarityResults([]); return; }
    const data = await api(`/api/taxpayer/similarity?q=${encodeURIComponent(query)}`);
    setSimilarityResults(data.results || []);
  };

  // BL 선택 변경 시 유사도검색 자동 실행
  useEffect(() => {
    const r = rows[selectedIdx];
    if (!r) return;
    const q = r.soldTo || r.shipTo || r.companyEn || '';
    setSimilarityQuery(q);
    if (q) runSimilarity(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdx, rows.length]);

  const applyCandidate = (cand) => {
    setRows(prev => prev.map((x, i) => i === selectedIdx ? {
      ...x,
      companyKr: cand.companyKr,
      businessNo: cand.businessNo,
      phone: cand.phone,
      email: cand.email,
      final: { ...(x.final || {}), businessNumber: cand.businessNo, matchedCompanyKr: cand.companyKr, status: 'FOUND_BIZ_NO' },
      status: 'FOUND_BIZ_NO',
      confirmed: true
    } : x));
  };

  const visibleRows = useMemo(() => {
    if (!showOnlyPending) return rows.map((r, i) => ({ r, i }));
    return rows.map((r, i) => ({ r, i })).filter(({ r }) => r.status !== 'FOUND_BIZ_NO');
  }, [rows, showOnlyPending]);

  const summary = useMemo(() => ({
    total: rows.length,
    found: rows.filter(r => r.status === 'FOUND_BIZ_NO').length,
    contact: rows.filter(r => r.status === 'CONTACT_ONLY').length,
    pending: rows.filter(r => r.status === 'PENDING' || r.status === 'NOT_FOUND').length
  }), [rows]);

  const selected = rows[selectedIdx];

  return (
    <div className="flex flex-col bg-[#F5F1EC] text-gray-900" style={{ height: 'calc(100vh - 64px)' }}>
      {/* 헤더 */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#1a1f2c] text-white">
        <div className="flex items-center gap-3">
          <span className="font-bold text-[15px] tracking-wide">UNL</span>
          <span className="text-xs text-gray-300">납세자 매칭 (하네스 1차 목업)</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2 py-1 bg-blue-600 rounded">전체 {summary.total}</span>
          <span className="px-2 py-1 bg-emerald-600 rounded">확정후보 {summary.found}</span>
          <span className="px-2 py-1 bg-amber-500 rounded">연락처확인 {summary.contact}</span>
          <span className="px-2 py-1 bg-rose-600 rounded">대기 {summary.pending}</span>
        </div>
      </div>

      {/* 툴바 */}
      <div className="flex items-center gap-2 px-4 py-2 bg-white border-b">
        <button onClick={runAll} disabled={loadingAll} className="flex items-center gap-1 px-3 py-1.5 bg-[#f5c518] text-black text-xs font-bold rounded hover:bg-yellow-400 disabled:opacity-50">
          {loadingAll ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} 전체 하네스 실행
        </button>
        <label className="flex items-center gap-1 text-xs ml-4">
          <input type="checkbox" checked={showOnlyPending} onChange={e => setShowOnlyPending(e.target.checked)} />
          미확정만 보기
        </label>
        <div className="ml-auto text-[11px] text-gray-500">
          입력 우선순위: <code>SOLD TO &gt; SHIP TO &gt; 수입자상호(영문)</code>
        </div>
      </div>

      {/* 상단 그리드 — BL 리스트 */}
      <div className="overflow-auto border-b bg-white" style={{ height: '45%' }}>
        <table className="text-xs" style={{ minWidth: '1600px', width: '100%', whiteSpace: 'nowrap' }}>
          <thead className="sticky top-0 bg-gray-100 text-gray-700">
            <tr>
              <th className="px-2 py-2 text-left w-10">No</th>
              <th className="px-2 py-2 text-left w-28">House B/L</th>
              <th className="px-2 py-2 text-left w-24">상태</th>
              <th className="px-2 py-2 text-left w-24">주소유형</th>
              <th className="px-2 py-2 text-left w-44">해외거래처</th>
              <th className="px-2 py-2 text-left w-44">수입자상호(영문)</th>
              <th className="px-2 py-2 text-left w-32">수입자 상호</th>
              <th className="px-2 py-2 text-left w-28">사업자번호</th>
              <th className="px-2 py-2 text-left w-28">전화번호</th>
              <th className="px-2 py-2 text-left w-40">EMAIL</th>
              <th className="px-2 py-2 text-left">주소</th>
              <th className="px-2 py-2 text-left w-24">액션</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(({ r, i }) => {
              const meta = STATUS_META[r.status] || STATUS_META.PENDING;
              const addrType = r.final?.addressType || '미확인';
              const isSel = i === selectedIdx;
              return (
                <tr key={r.blNo} onClick={() => setSelectedIdx(i)}
                    className={`border-b cursor-pointer ${isSel ? 'bg-yellow-50' : r.final?.addressMismatch ? 'bg-orange-50/60' : 'hover:bg-gray-50'}`}>
                  <td className="px-2 py-1.5">{i + 1}</td>
                  <td className="px-2 py-1.5 font-mono">{r.blNo}</td>
                  <td className="px-2 py-1.5">
                    {r.loading ? <Loader2 size={12} className="animate-spin inline" /> : <Badge text={meta.label} cls={meta.cls} />}
                  </td>
                  <td className="px-2 py-1.5"><Badge text={addrType} cls={ADDR_META[addrType] || ADDR_META['미확인']} /></td>
                  <td className="px-2 py-1.5 truncate max-w-[180px]" title={r.companyEn}>{r.companyEn}</td>
                  <td className="px-2 py-1.5 truncate max-w-[180px]" title={r.shipTo}>{r.shipTo}</td>
                  <td className="px-2 py-1.5 font-bold text-gray-800">{r.final?.matchedCompanyKr || '-'}</td>
                  <td className="px-2 py-1.5 text-blue-600 underline">{r.final?.businessNumber || '-'}</td>
                  <td className="px-2 py-1.5">{r.phone || '-'}</td>
                  <td className="px-2 py-1.5 truncate max-w-[160px]" title={r.email}>{r.email}</td>
                  <td className="px-2 py-1.5 truncate" title={r.address}>{r.address}</td>
                  <td className="px-2 py-1.5">
                    <button onClick={(e) => { e.stopPropagation(); runOne(i); }} className="px-1.5 py-0.5 text-[10px] bg-blue-500 text-white rounded hover:bg-blue-600">재실행</button>
                  </td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={12} className="p-8 text-center text-gray-500">샘플 로딩 중...</td></tr>}
          </tbody>
        </table>
      </div>

      {/* 하단 그리드 — 유사도검색 */}
      <div className="flex-1 flex flex-col bg-white overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b">
          <input
            value={similarityQuery}
            onChange={e => setSimilarityQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runSimilarity()}
            placeholder="영문상호/한글상호 검색"
            className="px-2 py-1 border rounded text-xs w-64"
          />
          <button onClick={() => runSimilarity()} className="px-3 py-1 bg-gray-800 text-white text-xs rounded flex items-center gap-1"><Search size={12} /> 검색</button>
          <button onClick={() => selected && applyCandidate(similarityResults[0] || {})} disabled={!similarityResults.length}
                  className="px-3 py-1 bg-emerald-500 text-white text-xs rounded disabled:opacity-40">적용</button>
          <label className="flex items-center gap-1 text-xs ml-3">
            <input type="checkbox" checked={customsOn} onChange={e => setCustomsOn(e.target.checked)} /> 관세청 DATA
          </label>
          <label className="flex items-center gap-1 text-xs">
            <input type="checkbox" checked={hometaxOn} onChange={e => setHometaxOn(e.target.checked)} /> 홈택스 DATA
          </label>
          {selected?.final?.warningMessage && (
            <span className="ml-auto flex items-center gap-1 text-[11px] text-orange-700 font-semibold">
              <AlertTriangle size={12} /> {selected.final.warningMessage}
            </span>
          )}
        </div>

        {/* 선택된 BL 상세 + AI Judge 사유 */}
        {selected && (
          <div className="px-3 py-2 bg-gray-50 border-b text-[11px] grid grid-cols-4 gap-3">
            <div><div className="text-gray-500">선택 B/L</div><div className="font-mono font-bold">{selected.blNo}</div></div>
            <div><div className="text-gray-500">매칭 한글상호</div><div className="font-bold">{selected.final?.matchedCompanyKr || '-'}</div></div>
            <div><div className="text-gray-500">신뢰도</div><div className="font-bold">{selected.final?.confidence ?? '-'}</div></div>
            <div><div className="text-gray-500">다음 액션</div><div>{selected.final?.nextAction || '-'}</div></div>
            {selected.final?.reasoning && (
              <div className="col-span-4 text-gray-600">
                <span className="text-gray-500">AI Judge 사유: </span>{selected.final.reasoning}
              </div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-auto">
          <table className="text-xs" style={{ minWidth: '1600px', width: '100%', whiteSpace: 'nowrap' }}>
            <thead className="sticky top-0 bg-gray-100">
              <tr>
                <th className="px-2 py-2 text-left w-10">No</th>
                <th className="px-2 py-2 text-left w-20">구분</th>
                <th className="px-2 py-2 text-left w-28">업체명</th>
                <th className="px-2 py-2 text-left w-40">업체명(영문)</th>
                <th className="px-2 py-2 text-left w-28">사업자번호</th>
                <th className="px-2 py-2 text-left w-28">전화번호</th>
                <th className="px-2 py-2 text-left w-28">휴대폰</th>
                <th className="px-2 py-2 text-left w-20">우편번호</th>
                <th className="px-2 py-2 text-left">주소</th>
                <th className="px-2 py-2 text-left w-40">EMAIL</th>
                <th className="px-2 py-2 text-left w-16">점수</th>
                <th className="px-2 py-2 text-left w-20">적용</th>
              </tr>
            </thead>
            <tbody>
              {similarityResults.map((c, i) => (
                <tr key={i} className="border-b hover:bg-blue-50">
                  <td className="px-2 py-1.5">{i + 1}</td>
                  <td className="px-2 py-1.5">{c.label || '유사도검색'}</td>
                  <td className="px-2 py-1.5 font-bold">{c.companyKr}</td>
                  <td className="px-2 py-1.5">{c.companyEn}</td>
                  <td className="px-2 py-1.5 text-blue-600 underline">{c.businessNo || '-'}</td>
                  <td className="px-2 py-1.5">{c.phone || '-'}</td>
                  <td className="px-2 py-1.5">{c.mobile || '-'}</td>
                  <td className="px-2 py-1.5">{c.zipcode || '-'}</td>
                  <td className="px-2 py-1.5 truncate" title={c.address}>{c.address}</td>
                  <td className="px-2 py-1.5 truncate max-w-[160px]" title={c.email}>{c.email}</td>
                  <td className="px-2 py-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${c.score >= 75 ? 'bg-emerald-100 text-emerald-700' : c.score >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{c.score}</span>
                  </td>
                  <td className="px-2 py-1.5">
                    <button onClick={() => applyCandidate(c)} className="px-1.5 py-0.5 text-[10px] bg-emerald-500 text-white rounded hover:bg-emerald-600 flex items-center gap-0.5"><CheckCircle2 size={10} /> 적용</button>
                  </td>
                </tr>
              ))}
              {!similarityResults.length && <tr><td colSpan={12} className="p-8 text-center text-gray-500">검색어를 입력하거나 위에서 BL을 선택하세요.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TaxpayerMatchingPanel;
